/**
 * Prepaid tier celebration.
 *
 * Why the animation is deliberately detached from the element that triggers it:
 *
 * Crossing the threshold normally happens on add-to-cart, and that fires TWO
 * renders. renderContents() replaces #CartDrawer immediately, then the cartUpdate
 * publish makes onCartUpdate() fetch the section again and replace
 * cart-drawer-items a few hundred ms later. Anything owned by <prepaid-tier-bar>
 * - a pending timer, a canvas inside .drawer__inner - gets destroyed by that
 * second render, and the replacement element sees the tier already recorded as
 * unlocked so it never re-fires. Net result: a flash, then nothing.
 *
 * So the element's only job is to detect the crossing. The canvas is mounted on
 * document.body, positioned over the drawer, and the animation runs at module
 * scope where no re-render can reach it.
 */
(() => {
  const STATE_KEY = 'prepaid-tier-unlocked';
  const CANVAS_CLASS = 'prepaid-confetti';
  const DURATION = 2600;

  let running = false;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Resolve the drawer's on-screen box, or null if it isn't visible yet. */
  function drawerRect() {
    const inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return null;
    const rect = inner.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 40) return null;
    // While the drawer slides in it sits off to the right of the viewport.
    if (rect.left >= window.innerWidth - 20) return null;
    return rect;
  }

  /**
   * Wait for the drawer to actually be on screen before firing. On add-to-cart
   * the drawer opens ~50ms after render and then transitions in.
   */
  function whenDrawerVisible(callback, timeout = 2500) {
    const start = performance.now();
    const tick = () => {
      const rect = drawerRect();
      if (rect) {
        callback(rect);
        return;
      }
      if (performance.now() - start > timeout) return;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function pulseBar() {
    const bar = document.querySelector('prepaid-tier-bar');
    if (!bar) return;
    bar.classList.add('prepaid-bar--celebrate');
    setTimeout(() => bar.classList.remove('prepaid-bar--celebrate'), 1600);
  }

  function burst(rect, dark, light) {
    const existing = document.querySelector('.' + CANVAS_CLASS);
    if (existing) existing.remove();

    const width = rect.width;
    const height = rect.height;

    const canvas = document.createElement('canvas');
    canvas.className = CANVAS_CLASS;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.left = rect.left + 'px';
    canvas.style.top = rect.top + 'px';
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const colors = [dark, light, light, dark, light, dark];
    const particles = [];

    // Two side bursts angled inwards, plus a lighter shower from the top.
    const origins = [
      { x: width * 0.08, y: height * 0.38, vx: 4.2, count: 55 },
      { x: width * 0.92, y: height * 0.38, vx: -4.2, count: 55 },
    ];

    origins.forEach((origin) => {
      for (let i = 0; i < origin.count; i++) {
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: origin.vx + (Math.random() - 0.5) * 5,
          vy: -(Math.random() * 9 + 5),
          w: Math.random() * 6 + 4,
          h: Math.random() * 5 + 4,
          rotation: Math.random() * Math.PI,
          spin: (Math.random() - 0.5) * 0.3,
          color: colors[(Math.random() * colors.length) | 0],
          life: 1,
        });
      }
    });

    for (let i = 0; i < 30; i++) {
      particles.push({
        x: Math.random() * width,
        y: -20 - Math.random() * 120,
        vx: (Math.random() - 0.5) * 1.6,
        vy: Math.random() * 2 + 1.5,
        w: Math.random() * 5 + 3,
        h: Math.random() * 4 + 3,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.2,
        color: colors[(Math.random() * colors.length) | 0],
        life: 1,
      });
    }

    const gravity = 0.23;
    const drag = 0.988;
    const start = performance.now();
    const fadeFrom = DURATION * 0.65;

    const frame = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);

      let visible = 0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.vy += gravity;
        p.vx *= drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;

        if (elapsed > fadeFrom) {
          p.life = Math.max(0, 1 - (elapsed - fadeFrom) / (DURATION - fadeFrom));
        }

        if (p.y > height + 24 || p.life <= 0) continue;
        visible++;

        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (visible > 0 && elapsed < DURATION) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
        running = false;
      }
    };

    requestAnimationFrame(frame);
  }

  function celebrate(options = {}) {
    if (running) return;
    if (options.force !== true && prefersReducedMotion()) return;
    running = true;

    const dark = options.dark || '#1A7F4B';
    const light = options.light || '#8FD6A9';

    pulseBar();
    whenDrawerVisible((rect) => burst(rect, dark, light));

    // Safety valve: if the drawer never became visible, release the lock.
    setTimeout(() => {
      if (!document.querySelector('.' + CANVAS_CLASS)) running = false;
    }, 3000);
  }

  // Manual trigger for testing from the browser console.
  window.prepaidTierCelebrate = (opts) => celebrate({ ...opts, force: true });

  if (!customElements.get('prepaid-tier-bar')) {
    class PrepaidTierBar extends HTMLElement {
      connectedCallback() {
        const unlocked = this.dataset.unlocked === 'true';
        const current = unlocked ? '1' : '0';

        let previous = null;
        try {
          previous = sessionStorage.getItem(STATE_KEY);
          sessionStorage.setItem(STATE_KEY, current);
        } catch (e) {
          return; // storage blocked; never celebrate rather than celebrate wrongly
        }

        if (previous === null) return; // first sighting this session
        if (previous === '1' || !unlocked) return; // not an upward crossing
        if (this.dataset.confetti !== 'true') return;

        celebrate({
          dark: this.dataset.accent,
          light: this.dataset.accentLight,
        });
      }
      // No disconnectedCallback on purpose. The animation must outlive this element.
    }

    customElements.define('prepaid-tier-bar', PrepaidTierBar);
  }
})();